
import axios from 'axios';
import { getOrCreateVisitorId } from '@/utils/cookieUtils';
import { BusinessData, Section, NotionResponse } from './types';
import { triggerNotionCloneWebhook } from './webhookService';

export const createNotionPage = async (businessData: BusinessData): Promise<NotionResponse> => {
  try {
    console.log('Creating Notion page with business data:', businessData);
    
    const visitorId = getOrCreateVisitorId();
    
    const notionPayload = {
      user_id: visitorId,
      brand_id: 'business-plans-db',
      page_title: `${businessData.businessName} Strategic Plan`,
      context: `${businessData.businessType} business focused on ${businessData.targetAudience}`,
      sections: businessData.uniqueSellingPoints.map(point => ({
        type: 'paragraph',
        content: point
      }))
    };
    
    const response = await axios.post('/api/test-apis/notion/createNotionPage', notionPayload);
    
    console.log('Notion API response:', response.data);
    
    return {
      success: true,
      data: response.data,
      pageUrl: response.data.page_url || `https://www.notion.so/Knowledge-1d2e21e1f5118044b82ce32c8f247af7?pvs=4&v=${Date.now()}`
    };
  } catch (error) {
    console.error('Error creating Notion page:', error);
    
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create Notion page',
      pageUrl: `https://www.notion.so/Knowledge-1d2e21e1f5118044b82ce32c8f247af7?pvs=4&v=${Date.now()}`
    };
  }
};

export const createDynamicNotionPage = async (
  messages: Array<{role: string, content: string}>,
  businessData: BusinessData,
  contactInfo: {
    contactName: string;
    contactEmail: string;
    contactPhone?: string;
  }
): Promise<NotionResponse> => {
  try {
    const visitorId = getOrCreateVisitorId();
    
    const chatContext = messages
      .map(msg => `${msg.role === 'user' ? 'Customer' : 'AI'}: ${msg.content}`)
      .join('\n\n');
    
    // Call webhook and get URL if available
    const webhookUrl = await triggerNotionCloneWebhook(visitorId, chatContext);
    console.log('Webhook URL received:', webhookUrl);
    
    // If we got a URL from the webhook, use it directly and return early
    if (webhookUrl) {
      console.log('Using URL directly from webhook:', webhookUrl);
      return {
        success: true,
        pageUrl: webhookUrl,
        data: { page_url: webhookUrl }
      };
    }
    
    const sections: Section[] = [
      {
        type: 'heading_1',
        content: `${businessData.businessName || 'Your Business'} Strategic Plan`
      },
      {
        type: 'heading_2',
        content: 'Business Overview'
      },
      {
        type: 'paragraph',
        content: `${businessData.businessName || 'Your Business'} operates in the ${businessData.businessType || 'General'} industry, focusing on ${businessData.targetAudience || 'General audience'}.`
      },
      {
        type: 'heading_2',
        content: 'Unique Selling Points'
      },
      ...(businessData.uniqueSellingPoints && businessData.uniqueSellingPoints.length > 0 
        ? businessData.uniqueSellingPoints.map(point => ({
            type: 'bulleted_list_item',
            content: point
          })) 
        : [{
            type: 'bulleted_list_item',
            content: 'Customized for your specific needs'
          }]
      ),
      {
        type: 'heading_2',
        content: 'Contact Information'
      },
      {
        type: 'paragraph',
        content: `Contact ${contactInfo.contactName} at ${contactInfo.contactEmail}${contactInfo.contactPhone ? ` or ${contactInfo.contactPhone}` : ''}`
      }
    ];
    
    const notionPayload = {
      user_id: visitorId,
      brand_id: 'business-plans-db',
      page_title: `${businessData.businessName || 'Your Business'} Strategic Plan`,
      context: chatContext,
      sections: sections
    };
    
    console.log('Sending payload to createNotionPage endpoint:', notionPayload);
    
    try {
      const response = await axios.post('/api/test-apis/notion/createNotionPage', notionPayload, {
        timeout: 15000,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Dynamic Notion page created:', response.data);
      
      if (!response.data.page_url) {
        console.warn('No page URL returned from Notion API, using fallback');
        return {
          success: true,
          pageUrl: `https://www.notion.so/Knowledge-1d2e21e1f5118044b82ce32c8f247af7?pvs=4&v=${Date.now()}`,
          fallback: true
        };
      }
      
      return {
        success: true,
        pageUrl: response.data.page_url,
        data: response.data
      };
    } catch (error) {
      console.error('Error creating dynamic Notion page:', error);
      return {
        success: true,
        pageUrl: `https://www.notion.so/Knowledge-1d2e21e1f5118044b82ce32c8f247af7?pvs=4&v=${Date.now()}`,
        error: error instanceof Error ? error.message : 'Failed to create Notion page',
        fallback: true
      };
    }
  } catch (error) {
    console.error('Error in createDynamicNotionPage:', error);
    return {
      success: true,
      pageUrl: `https://www.notion.so/Knowledge-1d2e21e1f5118044b82ce32c8f247af7?pvs=4&v=${Date.now()}`,
      error: error instanceof Error ? error.message : 'Failed to create Notion page',
      fallback: true
    };
  }
};
